import { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { ArrowSortDown20Regular, Dismiss20Regular } from '@fluentui/react-icons';

import { Debt } from '../../../../models/Entities/CashFlow/Debt/Debt';
import { DebtPaymentImputation } from '../../../../models/Entities/CashFlow/Payment/DebtPaymentImputation';
import { formatCurrencyAmountWithIssue } from '../../../../commons/currency/Currency.Functions';
import { agingBucket, currencyOf, daysSince } from '../../General/Cashflow.Dashboard.Functions';
import { autoImpute, imputationTotals, inPaymentCurrency, parseAmount, setImputation } from '../../CashFlow.Form.Logic';

const fmt = (amount: number, currency: string) => formatCurrencyAmountWithIssue(amount, currency);
const round2 = (value: number) => Math.round(value * 100) / 100;

interface ImputationPickerProps {
    debts: Debt[];
    paymentAmount: number;
    paymentCurrency: string;
    counterpartName: string;
    value: DebtPaymentImputation[];
    onChange: (imputations: DebtPaymentImputation[]) => void;
    error?: string;
}

/**
 * Qué deudas cancela el pago. Lista las pendientes de la contraparte, de la
 * más vieja a la más nueva, con el importe a aplicar editable. "Repartir" las
 * cubre en orden hasta agotar el pago.
 */
export const ImputationPicker = (props: ImputationPickerProps) => {
    // Tipo de cambio por moneda de deuda: unidades de la moneda del pago por unidad de la deuda.
    const [rates, setRates] = useState<Record<string, string>>({});
    const [texts, setTexts] = useState<Record<number, string>>({});

    const totals = imputationTotals(props.value, props.paymentAmount);

    // La cotización de una moneda arranca en 1; si el usuario la borra, no hay cotización.
    const rateTextOf = (code: string): string => (rates[code] === undefined ? '1' : rates[code]);
    const rateOf = (debt: Debt): number | undefined => {
        const debtCurrency = currencyOf(debt);
        if (debtCurrency === props.paymentCurrency) return 1;
        const value = parseAmount(rateTextOf(debtCurrency));
        return value > 0 ? value : undefined;
    };

    // Cambiar la cotización recalcula las deudas de esa moneda que ya están aplicadas.
    const changeRate = (code: string, text: string) => {
        setRates((all) => ({ ...all, [code]: text }));
        const value = parseAmount(text);
        if (!(value > 0)) return;
        const ids = new Set(props.debts.filter((debt) => currencyOf(debt) === code).map((debt) => debt.id));
        props.onChange(props.value.map((item) => (ids.has(item.debtId) ? { ...item, exchangeRate: value } : item)));
    };

    const current = (debt: Debt) => props.value.find((item) => item.debtId === debt.id);

    const apply = (debt: Debt, amountInDebtCurrency: number) => {
        const rate = rateOf(debt) ?? 0;
        const capped = Math.min(round2(amountInDebtCurrency), round2(debt.pendingAmount ?? 0));
        props.onChange(setImputation(props.value, { debtId: debt.id, amount: Math.max(0, capped), exchangeRate: rate }));
    };

    const toggle = (debt: Debt, on: boolean) => {
        if (!on) {
            apply(debt, 0);
            setTexts((all) => ({ ...all, [debt.id]: '' }));
            return;
        }
        // Al tildar, propone lo que falta del pago o lo que debe, lo que sea menor.
        const rate = rateOf(debt);
        const remainingInDebt = rate ? round2(Math.max(0, totals.remaining) / rate) : 0;
        const proposal = Math.min(debt.pendingAmount ?? 0, remainingInDebt > 0 ? remainingInDebt : (debt.pendingAmount ?? 0));
        apply(debt, proposal);
        setTexts((all) => ({ ...all, [debt.id]: String(round2(proposal)) }));
    };

    const spread = () => {
        const numericRates: Record<string, number> = {};
        Object.entries(rates).forEach(([code, text]) => { const value = parseAmount(text); if (value > 0) numericRates[code] = value; });
        const result = autoImpute({ debts: props.debts, paymentAmount: props.paymentAmount, paymentCurrency: props.paymentCurrency, rates: numericRates });
        props.onChange(result);
        const nextTexts: Record<number, string> = {};
        result.forEach((item) => { nextTexts[item.debtId] = String(item.amount); });
        setTexts(nextTexts);
    };

    const clear = () => { props.onChange([]); setTexts({}); };

    const foreignCurrencies = Array.from(new Set(props.debts.map(currencyOf).filter((code) => code !== props.paymentCurrency)));

    if (props.debts.length === 0) {
        return <span className="cff-empty">{props.counterpartName} no tiene deudas pendientes. El movimiento queda sin imputar y podés aplicarlo más adelante desde su detalle.</span>;
    }

    let totalsTone = 'cff-totals--warn';
    if (totals.over) totalsTone = 'cff-totals--bad';
    else if (Math.abs(totals.remaining) < 0.005) totalsTone = 'cff-totals--ok';

    return (
        <>
            <div className="cff-debt__tools">
                <button type="button" className="cff-btn" onClick={spread} disabled={!(props.paymentAmount > 0)} title="Cubre las deudas de la más vieja a la más nueva hasta agotar el importe">
                    <ArrowSortDown20Regular /> Repartir, más viejas primero
                </button>
                {props.value.length > 0 && (
                    <button type="button" className="cff-btn cff-btn--text" onClick={clear}><Dismiss20Regular /> Limpiar</button>
                )}
            </div>

            {foreignCurrencies.length > 0 && (
                <div className="cff-rates">
                    <span className="cff-label">Cotización</span>
                    {foreignCurrencies.map((code) => (
                        <label key={code} className="cff-debt__rate">
                            1 {code} = <input inputMode="decimal" placeholder="1" value={rateTextOf(code)} aria-label={`Cotización de ${code} en ${props.paymentCurrency}`} onChange={(event) => changeRate(code, event.target.value)} /> {props.paymentCurrency}
                        </label>
                    ))}
                    <span className="cff-help">Hay deudas en otra moneda que la del movimiento. Por defecto 1; cambiala si corresponde.</span>
                </div>
            )}

            <div className="cff-debts">
                {props.debts.map((debt) => {
                    const imputation = current(debt);
                    const on = !!imputation;
                    const debtCurrency = currencyOf(debt);
                    const rate = rateOf(debt);
                    const days = daysSince(debt.debtDate);
                    return (
                        <div key={debt.id} className={`cff-debt${on ? ' is-on' : ''}`}>
                            <input type="checkbox" checked={on} disabled={!rate} aria-label={`Aplicar a la deuda ${debt.id}`} onChange={(event) => toggle(debt, event.target.checked)} />
                            <div className="cff-debt__info">
                                <div className="cff-debt__line">
                                    <b className="cff-num">{fmt(debt.pendingAmount ?? 0, debtCurrency)}</b>
                                    <span className={`cff-tag cff-tag--${agingBucket(days)}`}>{days} días</span>
                                    {debt.settlementId && <Link className="cff-link" to={`/settlements/${debt.settlementId}`} target="_blank" rel="noopener noreferrer">LIQ-{debt.settlementId}</Link>}
                                </div>
                                <div className="cff-debt__line">
                                    <span>del {debt.debtDate ? moment(debt.debtDate).format('DD/MM/YYYY') : '—'}</span>
                                    {(debt.paidAmount ?? 0) > 0 && <span>· ya pagado {fmt(debt.paidAmount, debtCurrency)}</span>}
                                    {debt.comments && <span title={debt.comments}>· {debt.comments}</span>}
                                    {!rate && <span>· falta el tipo de cambio {debtCurrency}</span>}
                                </div>
                            </div>
                            <div className="cff-debt__apply">
                                <input
                                    inputMode="decimal"
                                    aria-label="Importe a aplicar"
                                    disabled={!on}
                                    value={on ? (texts[debt.id] ?? String(imputation?.amount ?? '')) : ''}
                                    onChange={(event) => {
                                        setTexts((all) => ({ ...all, [debt.id]: event.target.value }));
                                        apply(debt, parseAmount(event.target.value));
                                    }}
                                />
                                <span className="cff-debt__cur">{debtCurrency}</span>
                                {on && rate !== 1 && imputation && <span className="cff-help cff-num">≈ {fmt(inPaymentCurrency(imputation), props.paymentCurrency)}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={`cff-totals ${totalsTone}`}>
                <span>Aplicado a deudas: <b className="cff-num">{fmt(totals.imputed, props.paymentCurrency)}</b></span>
                {totals.over
                    ? <span className="cff-totals__rem">Se pasa por <b className="cff-num">{fmt(Math.abs(totals.remaining), props.paymentCurrency)}</b></span>
                    : totals.remaining > 0.005
                        ? <span className="cff-totals__rem">Queda sin imputar: <b className="cff-num">{fmt(totals.remaining, props.paymentCurrency)}</b></span>
                        : <span>Todo el importe queda aplicado.</span>}
            </div>
            {props.error && <div className="cff-error" role="alert">{props.error}</div>}
        </>
    );
};
